import '../index.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog, Button, TextField, DialogTitle } from '@material-ui/core';
import { Select } from '@components/SharedComponents/DesignSystem/Inputs';
import { EditGroupField } from './EditGroupField';
import { translate } from '@utils';
import { setPartialFilters } from '@actions/lettersPageActions';
import {
  canCreateSubmissionGroupSelector,
  canHaveGroupEditSelector,
  getSubmissionGroupIsCreating,
  getSubmissionGroupListSelector,
} from '@selectors/submissionGroups';
import { MgState, SubmissionQueryInterface, TranslationKey } from '@types';
import { ALL_GROUPS_OPTION, groupsFilterOptions } from '../constants';
import { AddRoundIcon, SettingsSmallIcon, WellDone, DeleteRoundIcon } from '@images/icons';
import { setCheckedSubmissions } from '@actions/submissionActions';
import { makeCreateSubmissionGroup, makeSubmissionGroups } from '@actions/submissionGroupsActions';

export const GroupsSelect = () => {
  const dispatch = useDispatch();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreatedGroup, setIsCreatedGroup] = useState(false);
  const [createdGroupName, setCreatedGroupName] = useState('');
  const [groupEditId, setGroupEditId] = useState(null);
  const [isCreateError, setIsCreateError] = useState(false);

  const submissionGroups = useSelector(getSubmissionGroupListSelector);
  const isCreating = useSelector(getSubmissionGroupIsCreating);
  const { groupId } = useSelector<MgState, SubmissionQueryInterface>(
    ({ lettersPage }) => lettersPage.filters
  );
  const canHaveGroupEdit = useSelector(canHaveGroupEditSelector);
  const canCreateGroup = useSelector(canCreateSubmissionGroupSelector);

  useEffect(() => {
    dispatch(makeSubmissionGroups());
  }, []);

  const handleChangeGroup = (value) => {
    if (!value) {
      return;
    }
    const updatedValue = value === ALL_GROUPS_OPTION ? '' : value;
    dispatch(setPartialFilters({ groupId: updatedValue }));
    dispatch(setCheckedSubmissions([]));
  };

  const renderOptions = () => {
    const basicGroupOptions = groupsFilterOptions.map((groupId: TranslationKey) => ({
      value: groupId,
      label: translate(groupId),
    }));

    const customOptions = submissionGroups.map((group) => ({
      value: group.id,
      label: group.name,
    }));

    return [...basicGroupOptions, ...customOptions];
  };

  const handleInputChange = ({ target }) => {
    setCreatedGroupName('');
    setCreatedGroupName(target.value);
    setIsCreatedGroup(true);
    setIsCreateError(submissionGroups.some((group) => group.name === target.value));
  };

  const handleCreateGroup = useCallback(() => {
    dispatch(makeCreateSubmissionGroup({ name: createdGroupName }));
    setIsCreatedGroup(false);
    setCreatedGroupName('');
  }, [submissionGroups, createdGroupName, isCreatedGroup, createdGroupName]);

  const handleAddGroupClick = () => {
    setCreatedGroupName('');
    setIsCreatedGroup(true);
  };

  const handleModalCloseClick = () => {
    setIsEditOpen(false);
    setGroupEditId(null);
    setIsCreatedGroup(false);
    setCreatedGroupName('');
  };
  const handleCancel = useCallback(() => {
    setIsEditOpen(false);
  }, [isEditOpen]);

  const handleEditModalOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();
    setIsEditOpen(true);
  };

  return (
    <>
      <Dialog open={isEditOpen} onClose={handleCancel} className="edit-group-modal">
        <DialogTitle className="edit-group-modal-title">
          {translate('edit_groups_modal')}
        </DialogTitle>

        <div className="create-group-input-container">
          {isCreatedGroup ? (
            <>
              <Button size="large" onClick={() => setIsCreatedGroup(false)} disableRipple>
                <DeleteRoundIcon />
              </Button>
              <TextField
                onChange={handleInputChange}
                value={createdGroupName}
                error={isCreateError}
                data-testid="new-group-input"
                helperText={isCreateError ? translate('group_name_exist') : ''}
              />
              <Button
                color="primary"
                disabled={!createdGroupName || isCreating || isCreateError}
                onClick={handleCreateGroup}
                disableRipple
                data-testid="new-group-create-done"
              >
                <WellDone />
              </Button>
            </>
          ) : (
            canCreateGroup && (
              <>
                <Button
                  size="large"
                  onClick={handleAddGroupClick}
                  disableRipple
                  data-testid="create-group-button"
                >
                  <AddRoundIcon />
                </Button>
                <span className="edit-group-field">{translate('create_new_group')}</span>
              </>
            )
          )}
        </div>
        <div className="edit-group-item-container">
          {submissionGroups.map(({ id, name, submissions }) => (
            <EditGroupField
              key={id}
              componentKey={id}
              submissions={submissions}
              groupId={id}
              groupName={name}
              submissionGroups={submissionGroups}
              toggleEdit={() =>
                setGroupEditId((groupEditId) => (groupEditId !== id ? id : undefined))
              }
              isEditable={id === groupEditId}
            />
          ))}
        </div>
        <div className="edit-group-dialog-actions">
          <Button color="secondary" onClick={handleModalCloseClick} disableRipple data-testid="edit-group-dialog-close-button">
            {translate('close')}
          </Button>
        </div>
      </Dialog>
      <Select
        options={renderOptions()}
        value={groupId ? groupId : ALL_GROUPS_OPTION}
        handleChange={(val) => handleChangeGroup(val)}
        variant="outlined"
        className="filter-select-type"
        customClass="MuiSelect-filter"
        testId="select-group"
        renderButton={() =>
          canHaveGroupEdit && (
            <div
              onClick={handleEditModalOpen}
              className="button-option"
              data-testid="edit-group-option"
            >
              <SettingsSmallIcon />
              {translate('edit_groups')}
            </div>
          )
        }
      />
    </>
  );
};
